import {
  CertificationOut,
  ComponentDetailsByFileOut,
  EPDConformityOut,
  ManufacturerOUT,
  ProductSearchInput,
  InternalProductSearchOutput,
  ProductSourceEnum,
  ProductTypeChoicesEnum,
  SearchEngineCategoryOUT,
  SimpleMaterialOut,
  categoriesAPI,
  componentsApi,
  getLocalAuthHeader,
  inventoryProductsApi,
  repositoriesApi,
  searchAPI,
} from "api-client";
import { AxiosError, AxiosResponse } from "axios";
import { DropdownOption } from "components/DropdownCheckbox";
import { makeAutoObservable } from "mobx";
import { buildingStore } from "store/Building/BuildingStore";
import { OTHER_PROPERTIES_OPTIONS } from "features/MappingEditor/consts";
import { MEStore } from "../MappingEditor/MappingEditorStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { OKBCategoryType } from "./Filters/OKBCategoryFilter";

const compliantInitialState = {
  hasMore: true,
  totalCompliant: 0,
  compliant: 0,
  nonCompliant: 0,
};

const SEARCH_PAGE_SIZE = 25;

interface OpenDetailsMenu {
  open: boolean;
  product?: InternalProductSearchOutput | null;
}

interface OpenSearchProps {
  state: boolean;
  term?: string;
}

export interface CompliantItemsInfo {
  hasMore: boolean;
  totalCompliant: number;
  compliant: number;
  nonCompliant: number;
}

export interface FiltersOptions {
  OKB_categories?: OKBCategoryType[];
  DIN_categories?: DropdownOption[];
  other_properties?: DropdownOption[];
  materials?: SimpleMaterialOut[];
  conformity?: EPDConformityOut[];
  certifications?: CertificationOut[];
  manufacturers?: ManufacturerOUT[];
}

export interface ProductsProps {
  count: number;
  items: InternalProductSearchOutput[];
}

export interface SearchTabsProps {
  id: string;
  type?: ProductTypeChoicesEnum;
  source?: string;
  hidden?: boolean;
}

export const NO_UUID = "00000000-0000-0000-0000-000000000000";

class SearchStore {
  filtersOptions: FiltersOptions = {
    OKB_categories: [],
    DIN_categories: [],
    other_properties: OTHER_PROPERTIES_OPTIONS,
    materials: [],
    conformity: [],
    certifications: [],
    manufacturers: [],
  };
  currentSearchPage = 1;
  searchInput: ProductSearchInput = {
    query: "",
  };
  openProductSearch: OpenSearchProps = { state: false, term: undefined };
  openEPDSearch: OpenSearchProps = { state: false, term: undefined };
  productSearchLoading = false;
  usingOrganisationSearch = true;
  selectedTab: SearchTabsProps | undefined = undefined;
  searchResults: ProductsProps = {
    count: 0,
    items: [],
  };
  compliantItemsInfo: CompliantItemsInfo = compliantInitialState;
  searchError: boolean = false;
  currentDetailNode: ComponentDetailsByFileOut | undefined = undefined;
  openDetails: OpenDetailsMenu = {
    open: false,
    product: undefined,
  };
  componentDetails: ComponentDetailsByFileOut | undefined = undefined;
  navigationIsSticky: boolean = false;
  usedProducts: ProductsProps = {
    count: 0,
    items: [],
  };
  isUsedSearch: boolean | undefined = undefined;
  hasAnyUsedProducts: boolean = false;
  canCreate = true;

  setOpenProductSearch(openProductSearch: OpenSearchProps) {
    this.openProductSearch = openProductSearch;
  }

  setOpenEPDSearch(openEPDSearch: OpenSearchProps) {
    this.openEPDSearch = openEPDSearch;
  }

  setSelectedTab(selectedTab: SearchTabsProps | undefined) {
    this.selectedTab = selectedTab;
  }

  setSearchInput(searchInput: ProductSearchInput) {
    this.searchInput = searchInput;
  }

  setUsingOrganisationSearch(usingOrganisationSearch: boolean) {
    this.usingOrganisationSearch = usingOrganisationSearch;
  }

  setProductSearchLoading(productSearchLoading: boolean) {
    this.productSearchLoading = productSearchLoading;
  }

  resetSearchResults() {
    this.searchResults = { count: 0, items: [] };
  }

  setSearchResults(searchResults: ProductsProps) {
    this.searchResults = searchResults;
  }

  updateSearchResults(
    count: number,
    newProducts: InternalProductSearchOutput[]
  ) {
    this.searchResults = {
      count: count,
      items: this.searchResults.items.concat(newProducts),
    };
  }

  setCurrentSearchPage(page: number) {
    this.currentSearchPage = page;
  }

  setCompliantItemsInfo(compliantItemsInfo: CompliantItemsInfo) {
    this.compliantItemsInfo = compliantItemsInfo;
  }

  setSearchError(searchError: boolean) {
    this.searchError = searchError;
  }

  resetCompliantItemsInfo() {
    this.setCompliantItemsInfo(compliantInitialState);
  }

  setCurrentDetailNode(currentComponentNode?: ComponentDetailsByFileOut) {
    this.currentDetailNode = currentComponentNode;
  }

  setFilterOptions(filtersOptions: FiltersOptions) {
    this.filtersOptions = filtersOptions;
  }

  setOpenDetails(openDetails: OpenDetailsMenu) {
    this.openDetails = openDetails;
  }

  setComponentDetails(componentDetails: ComponentDetailsByFileOut | undefined) {
    this.componentDetails = componentDetails;
  }

  setNavigationIsSticky(navigationIsSticky: boolean) {
    this.navigationIsSticky = navigationIsSticky;
  }

  setIsUsedSearch(isUsedSearch: boolean | undefined) {
    this.isUsedSearch = isUsedSearch;
  }

  setHasAnyUsedProducts(hasAnyUsedProducts: boolean) {
    this.hasAnyUsedProducts = hasAnyUsedProducts;
  }

  updateCompliantInfo(currentCount: number, totalCount: number) {
    if (this.usingOrganisationSearch) return;
    const compliantHasReachedEnd =
      this.compliantItemsInfo.hasMore && currentCount === totalCount;

    if (compliantHasReachedEnd) {
      this.setCompliantItemsInfo({
        ...this.compliantItemsInfo,
        hasMore: false,
        totalCompliant: totalCount,
      });
      this.setCurrentSearchPage(0);
    }

    const isStillLoading = this.compliantItemsInfo.hasMore;
    const compliant = isStillLoading
      ? this.searchResults.items.length
      : this.compliantItemsInfo.totalCompliant;
    const nonCompliant = isStillLoading
      ? 0
      : this.searchResults.items.length -
        this.compliantItemsInfo.totalCompliant;

    this.setCompliantItemsInfo({
      ...this.compliantItemsInfo,
      compliant,
      nonCompliant,
    });
  }

  resetForOrganisationSearch() {
    if (this.usingOrganisationSearch) return;
    buildingStore.setCurrentBuilding(undefined);
    buildingStore.setBuildingID(undefined);
    this.setIsUsedSearch(undefined);
    this.setSelectedTab(undefined);
    this.usingOrganisationSearch = true;
    this.resetSearchResults();
    this.setCurrentSearchPage(1);
    this.resetCompliantItemsInfo();
  }

  async getProducts(page?: number) {
    if (this.currentSearchPage === 0) this.currentSearchPage = 1;
    const pageNumber = page ?? this.currentSearchPage;
    const authHeader = getLocalAuthHeader();
    const searchInputs: ProductSearchInput = {
      ...this.searchInput,
      building_id: buildingStore.buildingID,
      product_source: this.selectedTab?.source as ProductSourceEnum,
      building_compliance: this.compliantItemsInfo.hasMore,
      type: this.selectedTab?.type,
      organisation_id:
        sidebarMenuStore.organisationID ??
        sidebarMenuStore.currentOrganisation?.id,
    };
    return await searchAPI
      .inventoryApiV1RoutersSearchEngineSearchProducts(
        searchInputs,
        pageNumber,
        SEARCH_PAGE_SIZE,
        authHeader
      )
      .then((res: AxiosResponse) => {
        const data = res.data;
        if (data) {
          this.updateSearchResults(
            data.total_count_without_compliance_filter,
            data.items
          );
          this.setCurrentSearchPage(pageNumber);
        } else {
          this.resetSearchResults();
        }
        this.updateCompliantInfo(this.searchResults.items.length, data.count);
        this.setSearchError(false);
      })
      .catch((error: AxiosError) => {
        console.error(error, "error.SearchEngineSearchProducts");
        MEStore.setErrorMessage((error as Error).message);
        this.setSearchError(true);
      });
  }

  async checkUsedProductsStatus() {
    const count = await this.getUsedProducts(1, true);
    const hasAnyUsedProducts = Boolean(Number(count) > 0);
    this.setHasAnyUsedProducts(hasAnyUsedProducts);
    return hasAnyUsedProducts;
  }

  async getUsedProducts(page?: number, getResponseCount?: boolean) {
    // if (this.currentSearchPage === 0) this.currentSearchPage = 1;
    const pageNumber = page ?? this.currentSearchPage;
    const orgID =
      sidebarMenuStore.organisationID ??
      sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    const authHeader = getLocalAuthHeader();
    return await searchAPI
      .inventoryApiV1RoutersSearchEngineSearchUsedProducts(
        orgID,
        this.usingOrganisationSearch ? null : buildingStore.buildingID, // Building ID
        this.searchInput.query,
        pageNumber,
        SEARCH_PAGE_SIZE,
        authHeader
      )
      .then((response) => {
        const data = response.data;
        if (getResponseCount) {
          return data.items.length;
        }
        if (data) {
          this.updateSearchResults(
            data.total_count_without_compliance_filter,
            data.items
          );
          this.setCurrentSearchPage(pageNumber);
        } else {
          this.resetSearchResults();
        }
        this.setSearchError(false);
      })
      .catch((error: AxiosError) => {
        console.error(error, "error.SearchUsedProducts");
        this.setSearchError(true);
      });
  }

  async getDinCategories() {
    const authHeader = getLocalAuthHeader();
    return await searchAPI
      .inventoryApiV1RoutersSearchEngineProductDinCategories(
        undefined,
        authHeader
      )
      .then((response) => {
        this.setFilterOptions({
          ...this.filtersOptions,
          DIN_categories: response.data?.map((item) => createOptionItem(item)),
        });
      })
      .catch((error) => {
        console.error("getDinCategories.error", error);
      });

    function createOptionItem(item: SearchEngineCategoryOUT): DropdownOption {
      return {
        id: item.id,
        name: `${item.category_number} ${item.name}`,
        value: item.category_number,
        children: item?.children
          ?.map((rec) => createOptionItem(rec))
          .sort((a, b) => a.name.localeCompare(b.name)),
      } as DropdownOption;
    }
  }

  async getProductDetails(productId: string, ifcFileId?: string) {
    const ifCID = ifcFileId ?? NO_UUID;
    const authHeader = getLocalAuthHeader();
    return inventoryProductsApi
      .inventoryApiV1RoutersProductsDetailsByFile(productId, ifCID, authHeader)
      .then((response) => {
        return response.data;
      })
      .catch((err) =>
        console.error(err, "err.inventoryApiV1RoutersProductsDetailsByFile")
      );
  }

  async getComponentDetails(componentId: string, ifcFileId?: string) {
    const ifCID = ifcFileId ?? NO_UUID;
    const authHeader = getLocalAuthHeader();
    return componentsApi
      .inventoryApiV1RoutersComponentsDetailsByFile(
        componentId,
        ifCID,
        authHeader
      )
      .then((response) => {
        this.setComponentDetails(response.data);
        return response.data;
      })
      .catch((err) =>
        console.error(err, "err.inventoryApiV1RoutersComponentsDetailsByFile")
      );
  }

  async getManufacturers() {
    const authHeader = getLocalAuthHeader();
    return await repositoriesApi
      .caApiV1RoutersRepositoryGetManufacturers(authHeader)
      .then((response) => {
        return response.data;
      })
      .catch((error) => console.log("getManufacturers.error", error));
  }

  async getCategories(categorySystem: string) {
    const authHeader = getLocalAuthHeader();
    return await categoriesAPI
      .caApiV1RoutersCategoryCategories(categorySystem, authHeader)
      .then((response) => {
        return response.data;
      })
      .catch((error) => console.log("getCategories.error", error));
  }

  hasAnyFilters() {
    return (
      this.searchInput.query ||
      this.searchInput.certifications?.length ||
      this.searchInput.din_category?.length ||
      this.searchInput.epd_conformity ||
      this.searchInput.material_name?.length
    );
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: SearchStore;

  static getInstance(): SearchStore {
    if (!SearchStore.instance) {
      SearchStore.instance = new SearchStore();
    }
    return SearchStore.instance;
  }
}
export const searchStore = SearchStore.getInstance();
